class ConstructorUtils {
  /**
   * Format money value from Constructor.io API (already in dollars)
   * @param {number|string} dollars - Amount in dollars (not cents)
   * @param {string|object} options - Format string or options object
   * @returns {string} Formatted money string
   */
  static formatMoney(dollars, options = {}) {
    if (typeof dollars === "string") {
      dollars = parseFloat(dollars);
    }

    if (isNaN(dollars) || dollars == null) return "$0.00";

    if (typeof options === "string") {
      options = { format: options };
    }

    if (options.withCurrency) {
      return this.formatMoneyWithCurrency(dollars);
    }

    const format =
      options.format || window.Shopify?.money_format || "${{amount}}";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (
      number,
      precision = 2,
      thousands = ",",
      decimal = "."
    ) => {
      if (isNaN(number) || number == null) return "0";

      number = Number(number).toFixed(precision);
      const parts = number.split(".");
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`
      );
      const centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    };

    const formatType = format.match(placeholderRegex)?.[1];
    let value = "";

    switch (formatType) {
      case "amount":
        value = formatWithDelimiters(dollars, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(dollars, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(dollars, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(dollars, 0, ".", ",");
        break;
      default:
        value = formatWithDelimiters(dollars, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  /**
   * Format money with currency symbol using Intl.NumberFormat
   * @param {number} dollars - Amount in dollars (not cents)
   * @returns {string} Formatted money with currency
   */
  static formatMoneyWithCurrency(dollars) {
    const amount = Number(dollars);
    const currencyCode =
      window.theme?.fakeDiscount?.currentMarket?.currency || "USD";

    try {
      const formatted = new Intl.NumberFormat(
        window.Shopify?.locale || "en-US",
        {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      ).format(amount);

      return formatted
        .replace(/\s?CA$/, "")
        .replace(/\s?US$/, "")
        .replace(/\s?USD$/, "")
        .replace(/\s?CAD$/, "");
    } catch (error) {
      return this.formatMoney(amount);
    }
  }

  /**
   * Build sized image URL with Shopify CDN query parameters
   * @param {string} url - Original image URL
   * @param {number} width - Target width
   * @param {number} height - Target height
   * @returns {string} Transformed image URL
   */
  static buildSizedImageUrl(url, width, height) {
    if (!url) return "";

    if (url.includes("width=") && url.includes("height=")) {
      return url;
    }

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}width=${width}&height=${height}&crop=center`;
  }

  /**
   * Get theme configuration value by key
   * @param {string} key - Configuration key
   * @returns {any} Configuration value
   */
  static getThemeConfig(key) {
    if (!key || !window.theme) return null;

    const keys = key.split(".");
    let value = window.theme;

    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        return null;
      }
    }

    return value;
  }

  /**
   * Process media images from product data
   * @param {object} product - Product object
   * @returns {Array} Processed media images array
   */
  static processMediaImages(product) {
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }

    const constructorData =
      product.constructorData?.data || product.constructorData || {};
    const rawMedia = constructorData.mediaImages;

    if (Array.isArray(rawMedia)) {
      return rawMedia;
    }

    return rawMedia ? [rawMedia] : [];
  }

  /**
   * Get image dimensions based on orientation setting
   * @param {string} orientation - Image orientation (square, landscape, portrait)
   * @param {object} options - Additional options (defaultWidth, defaultHeight)
   * @returns {object} Width and height dimensions
   */
  static getImageDimensions(orientation = "square", options = {}) {
    const { defaultWidth = 600, defaultHeight = 600 } = options;

    switch (orientation) {
      case "landscape":
        return { width: defaultWidth, height: Math.round(defaultWidth * 0.8) };
      case "portrait":
        return {
          width: Math.round(defaultHeight * 0.8),
          height: defaultHeight,
        };
      case "square":
      default:
        return { width: defaultWidth, height: defaultHeight };
    }
  }

  /**
   * Validate product object for required fields
   * @param {object} product - Product object to validate
   * @returns {object} Validation result with isValid and errors
   */
  static validateProduct(product) {
    if (!product) {
      return { isValid: false, errors: ["Product is null or undefined"] };
    }

    const errors = [];

    if (!product.title && !product.handle) {
      errors.push("Product missing title and handle");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Normalize color names for consistent comparison
   * @param {string} colorName - Raw color name
   * @returns {string} Normalized color name
   */
  static normalizeColorName(colorName) {
    if (!colorName || typeof colorName !== "string") return "";

    return colorName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, "")
      .replace(/\s+/g, "");
  }

  /**
   * Generate variant swatches from Constructor.io product data
   * @param {object} product - Product data from Constructor.io
   * @param {object} options - Options for swatch generation
   * @returns {object} Swatches result with HTML and metadata
   */
  static generateVariantSwatches(product, options = {}) {
    const swatchSettings = this.getThemeConfig("variantSwatches");

    if (!swatchSettings?.enabled) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const variations = product.variations || product.variants || [];
    if (!variations || variations.length === 0) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const uniqueColors = new Map();
    const imgOrientation =
      this.getThemeConfig("productCard.imgOrientation") || "square";
    const dimensions = this.getImageDimensions(imgOrientation);
    const constructorData =
      product.constructorData?.data ||
      product.constructorData ||
      product.data ||
      {};
    const mediaImages = Array.isArray(constructorData.mediaImages)
      ? constructorData.mediaImages
      : [];

    variations.forEach((variation) => {
      const data = variation.data || variation;
      let color = data.color || data.colour || data.metal;

      if (!color && data.title) {
        const titleParts = data.title.split(/[\/,]/).map((s) => s.trim());
        color = titleParts[titleParts.length - 1];
      }

      if (color && !uniqueColors.has(color)) {
        let rawImageUrl = data.image_url || data.imageUrl || "";

        if (!rawImageUrl && mediaImages.length > 0) {
          const variantImage = mediaImages.find((img) =>
            img.variant_ids?.includes(data.variation_id || data.id)
          );
          rawImageUrl = variantImage?.transformedSrc || variantImage?.src || "";
        }

        if (!rawImageUrl && mediaImages.length > 0) {
          rawImageUrl =
            mediaImages[0]?.transformedSrc || mediaImages[0]?.src || "";
        }

        const sizedImageUrl = rawImageUrl
          ? this.buildSizedImageUrl(
              rawImageUrl,
              dimensions.width,
              dimensions.height
            )
          : "";

        uniqueColors.set(color, {
          color: color,
          image_url: sizedImageUrl,
          inventory_quantity: data.inventory_quantity || 0,
          variation_id: data.variation_id || data.id,
          title: data.title,
          available: (data.inventory_quantity || 0) > 0,
        });
      }
    });

    const colorArray = Array.from(uniqueColors.keys());
    if (colorArray.length === 0) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const maxVisible = swatchSettings.maxVisible || 4;
    const useVariantImages = swatchSettings.useVariantImages || false;

    let swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-5" role="list" aria-label="Color options">
    `;

    colorArray.slice(0, maxVisible).forEach((color) => {
      const colorData = uniqueColors.get(color);

      swatchesHTML += `
        <li class="p-1" role="listitem">
          <button class="btn${
            colorData.available ? "" : " swatch-unavailable"
          }" 
                  type="button"
                  data-img="${colorData.image_url || ""}"
                  data-img-width="${dimensions.width}"
                  data-img-height="${dimensions.height}"
                  data-variant-available="${colorData.available}"
                  data-color-value="${color}"
                  data-action="change-swatch"
                  aria-label="Select ${color} color option"
                  title="${color} - ${
        colorData.image_url ? "Has image" : "No image"
      }">
      `;

      if (useVariantImages && colorData.image_url) {
        swatchesHTML += `
          <img class="img-fluid swatch-variant-image"
               src="${colorData.image_url}"
               alt="${color}"
               width="40" height="40"
               loading="lazy" aria-hidden="true">
        `;
      } else {
        const swatchImageName = color.toLowerCase().replace(/\s+/g, "-");
        const shopifySwatchUrl = `https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${swatchImageName}.png?width=24&height=24&crop=center`;

        swatchesHTML += `
          <img class="img-fluid swatch-custom-image"
               src="${shopifySwatchUrl}"
               alt="${color}"
               width="24" height="24"
               loading="lazy" aria-hidden="true"
               onerror="this.style.display='none'">
        `;
      }

      swatchesHTML += `
          </button>
        </li>
      `;
    });

    if (colorArray.length > maxVisible) {
      const remainingCount = colorArray.length - maxVisible;
      swatchesHTML += `
        <li class="color-swatch-more" role="listitem">
          <button type="button" 
                  class="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 32px; height: 32px; font-size: 10px;"
                  title="Show ${remainingCount} more colors"
                  aria-label="Show ${remainingCount} more color options"
                  data-action="show-all-swatches">
            +${remainingCount}
          </button>
        </li>
      `;
    }

    swatchesHTML += `
        </ul>
      </div>
    `;

    return {
      shouldShow: true,
      swatchesHTML: swatchesHTML,
      totalColors: colorArray.length,
      visibleColors: Math.min(colorArray.length, maxVisible),
    };
  }

  /**
   * Generate product badges using Constructor.io data
   * @param {object} product - Product data from Constructor.io
   * @param {boolean} hasDiscount - Whether product has a discount
   * @returns {object} Badge result with HTML and metadata
   */
  static generateProductBadge(product, hasDiscount = false) {
    const data = product.constructorData?.data || product.data || {};

    // Priority hierarchy: custom > preorder > new_preorder > promo > commercial

    // 1. Custom badge (highest priority - shows even on preorder products)
    const customBadgeSettings = this.getThemeConfig("customBadge");
    if (
      customBadgeSettings?.globalEnabled &&
      customBadgeSettings?.marketSettings?.enabled
    ) {
      return this._generateCustomBadge(customBadgeSettings.marketSettings);
    }

    // 2. Preorder badge
    if (data.preorder_tag) {
      const preorderSettings = this.getThemeConfig("preorderBadge");
      if (preorderSettings?.enabled) {
        return this._generatePreorderBadge(preorderSettings);
      }
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "preorder-disabled",
      };
    }

    // 3. New preorder badge
    if (data.new_preorder_tag) {
      const newPreorderSettings = this.getThemeConfig("newPreorderBadge");
      if (newPreorderSettings?.enabled) {
        return this._generateNewPreorderBadge(newPreorderSettings);
      }
    }

    // 4. Promo badge (if has discount)
    const promoBadgeSettings = this.getThemeConfig("promoBadge");
    if (promoBadgeSettings?.enabled && hasDiscount) {
      return this._generatePromoBadge(product, promoBadgeSettings);
    }

    // 5. Commercial badge
    const commercialBadgeSettings = this.getThemeConfig("commercialBadge");
    if (commercialBadgeSettings?.enabled) {
      return this._generateCommercialBadge(data, commercialBadgeSettings);
    }

    return { shouldShow: false, badgeHTML: null, badgeType: "none" };
  }

  /**
   * Generate commercial badge using Constructor.io boolean flags
   */
  static _generateCommercialBadge(data, settings) {
    let badgeType = null;
    let badgeSettings = null;

    if (data.waterproof_tag) {
      badgeType = "waterproof";
      badgeSettings = settings.waterproof;
    } else if (data.new_tag) {
      badgeType = "new";
      badgeSettings = settings.new;
    } else if (data.bestseller_tag) {
      badgeType = "bestseller";
      badgeSettings = settings.bestseller;
    } else if (data.low_inventory_tag) {
      badgeType = "lowInventory";
      badgeSettings = settings.lowInventory;
    }

    if (!badgeType || !badgeSettings?.text) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "commercial-no-match",
      };
    }

    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--commercial">
        <span class="badge badge--commercial badge--${badgeType}"
              style="background-color: ${badgeSettings.bgColor}; 
                     color: ${badgeSettings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}%; 
                     height: ${settings.height}%;">
          ${badgeSettings.text}
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML,
      badgeType: `commercial-${badgeType}`,
    };
  }

  /**
   * Generate promo badge based on discount percentage
   */
  static _generatePromoBadge(product, settings) {
    const data = product.constructorData?.data || product.data || {};
    const compareAtPrice = data.compareAtPrice || 0;
    const price = data.price || 0;

    if (!compareAtPrice || compareAtPrice <= price) {
      return { shouldShow: false, badgeHTML: null, badgeType: "no-discount" };
    }

    const savingsPercentage = Math.round(
      ((compareAtPrice - price) * 100) / compareAtPrice
    );
    const remainder = savingsPercentage % 5;
    const roundedPercentage =
      remainder > 2
        ? savingsPercentage + 5 - remainder
        : savingsPercentage - remainder;

    const opacity = (settings.opacity || 100) / 100;
    const bgColor = settings.bgColor || "#ff0000";
    const rgbMatch = bgColor.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    const r = rgbMatch ? parseInt(rgbMatch[1], 16) : 255;
    const g = rgbMatch ? parseInt(rgbMatch[2], 16) : 0;
    const b = rgbMatch ? parseInt(rgbMatch[3], 16) : 0;

    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--promo">
        <span class="badge badge--promo"
              style="padding: ${settings.padding || 8}px;
                     width: ${settings.width || 60}px;
                     height: ${settings.height || 60}px;
                     font-size: ${settings.fontSize || 12}px;
                     background-color: rgba(${r}, ${g}, ${b}, ${opacity});
                     color: ${settings.textColor || "#ffffff"};
                     border-radius: ${settings.borderRadius || 50}px;">
          ${roundedPercentage}% OFF
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML,
      badgeType: "promo",
      discountPercentage: roundedPercentage,
    };
  }

  /**
   * Generate custom badge
   */
  static _generateCustomBadge(settings) {
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--custom">
        <span class="badge badge--custom"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}px; 
                     height: ${settings.height}px;">
          ${settings.text}
        </span>
      </div>
    `;

    return { shouldShow: true, badgeHTML };
  }

  /**
   * Generate pre-order badge
   */
  static _generatePreorderBadge(settings) {
    const opacity = (settings.opacity || 100) / 100;
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--preorder">
        <span class="badge badge--preorder"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${opacity}; 
                     padding: ${settings.padding}px;">
          ${settings.text}
        </span>
      </div>
    `;

    return { shouldShow: true, badgeHTML, badgeType: "preorder" };
  }

  /**
   * Generate new pre-order badge
   */
  static _generateNewPreorderBadge(settings) {
    const opacity = (settings.opacity || 100) / 100;
    const badgeHTML = `
      <div class="badge-wrapper badge-wrapper--new-preorder">
        <span class="badge badge--new-preorder"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${opacity}; 
                     padding: ${settings.padding}px;">
          ${settings.text}
        </span>
      </div>
    `;

    return { shouldShow: true, badgeHTML, badgeType: "new-preorder" };
  }

  /**
   * Generate fake discount message from Constructor.io product data
   * @param {object} product - Product data from Constructor.io
   * @param {number} compareAtPrice - Compare at price
   * @param {number} price - Current price
   * @returns {object} Fake discount result with HTML and metadata
   */
  static generateFakeDiscountMessage(product, compareAtPrice, price) {
    const fakeDiscountSettings = this.getThemeConfig("fakeDiscount");
    const marketSettings = fakeDiscountSettings?.marketSettings;

    if (!marketSettings?.enabled) {
      return { shouldShow: false, messageHTML: null, discountPercentage: 0 };
    }

    if (!compareAtPrice || compareAtPrice <= price) {
      return { shouldShow: false, messageHTML: null, discountPercentage: 0 };
    }

    // Calculate discount percentage
    const rawPercentage = Math.round(
      ((compareAtPrice - price) * 100) / compareAtPrice
    );

    // Round to nearest 5
    const remainder = rawPercentage % 5;
    const discountPercentage =
      remainder > 2 ? rawPercentage + 5 - remainder : rawPercentage - remainder;

    // Generate message
    const messageTemplate =
      marketSettings.message || "Get [percentage]% OFF on this item!";
    const message = messageTemplate.replace(
      /\[percentage\]/g,
      discountPercentage
    );
    const color = marketSettings.messageColor || "#dc3545";

    const messageHTML = `
      <div class="fake-discount-message mt-2 p-2 rounded-2" 
           style="color: ${color}; font-size: 0.875rem; line-height: 1.2;">
        ${message}
      </div>
    `;

    return {
      shouldShow: true,
      messageHTML,
      discountPercentage,
    };
  }

  /**
   * Get current market data from theme configuration
   * @returns {object} Current market configuration
   */
  static getCurrentMarketData() {
    const fakeDiscountSettings = this.getThemeConfig("fakeDiscount") || {};

    return {
      currency: fakeDiscountSettings.currentMarket?.currency || "USD",
      locale: fakeDiscountSettings.currentMarket?.locale || "en-US",
      market: fakeDiscountSettings.currentMarket || {},
    };
  }

  /**
   * Render a complete product card HTML
   * Unified template for search, browse, and recommendations
   * @param {object} product - Product data from Constructor.io
   * @param {object} options - Rendering options
   * @returns {string} Complete product card HTML
   */
  static renderProductCard(product, options = {}) {
    if (!product) return "";

    const config = options.config || this._getDefaultConfig();
    const { imgWidth, imgHeight, aspectRatio } = config;
    const productTitle = product.title || product.value || "";
    const productHandle = product.handle || "";
    const productUrl =
      product.url ||
      product.constructorData?.url ||
      `/products/${productHandle}`;
    const firstVariant = (product.variants && product.variants[0]) || {};
    const constructorData =
      product.constructorData?.data || product.constructorData || {};
    const rawPrimarySrc =
      constructorData.image_url ||
      constructorData.mediaImages?.[0]?.transformedSrc ||
      product.featured_image?.src ||
      "";
    const rawSecondarySrc =
      constructorData.mediaImages?.[1]?.transformedSrc ||
      product.images?.[1]?.transformedSrc ||
      null;
    const mediaImages = constructorData.mediaImages || product.images || [];

    const productIndex = options.productIndex || 0;
    const isAboveTheFold = productIndex < 6;

    const getBaseUrl = (url) => {
      if (!url) return "";
      const qIndex = url.indexOf("?");
      return qIndex > 0 ? url.substring(0, qIndex) : url;
    };

    const primaryBaseUrl = getBaseUrl(rawPrimarySrc);
    const secondaryBaseUrl = getBaseUrl(rawSecondarySrc);

    const primarySrc = primaryBaseUrl
      ? `${primaryBaseUrl}?width=${imgWidth}&height=${imgHeight}`
      : "";
    const primarySrcset = primarySrc
      ? `${primarySrc} 1x, ${primaryBaseUrl}?width=${imgWidth * 2}&height=${
          imgHeight * 2
        } 2x`
      : "";

    const secondarySrc = secondaryBaseUrl
      ? `${secondaryBaseUrl}?width=${imgWidth}&height=${imgHeight}`
      : null;
    const secondarySrcset = secondarySrc
      ? `${secondarySrc} 1x, ${secondaryBaseUrl}?width=${imgWidth * 2}&height=${
          imgHeight * 2
        } 2x`
      : null;

    const loadingStrategy = isAboveTheFold ? "eager" : "lazy";
    const fetchPriority = isAboveTheFold && productIndex < 2 ? "high" : "auto";
    const imageAlt =
      mediaImages[0]?.altText || product.featured_image?.alt || productTitle;
    const finalPrice =
      product.price || constructorData.price || firstVariant.price || 0;
    const originalPrice =
      product.compare_at_price ||
      constructorData.compareAtPrice ||
      firstVariant.compare_at_price ||
      0;
    const isOnSale = originalPrice && originalPrice > finalPrice;

    const fakeDiscountResult = this.generateFakeDiscountMessage(
      product,
      originalPrice,
      finalPrice
    );
    const discountPercentage = fakeDiscountResult.discountPercentage;

    const primaryBadgeResult = this.generateProductBadge(
      product,
      isOnSale,
      discountPercentage > 0
    );
    const showSaleBadge = config.showSaleBadge && isOnSale;
    const saleBadgeColor = config.saleBadgeColor;
    const swatchesResult = this.generateVariantSwatches(product);
    const formattedPrice = this.formatMoney(finalPrice);
    const formattedComparePrice =
      isOnSale && originalPrice ? this.formatMoney(originalPrice) : "";
    const marginClass = config.cardStyle !== "card" ? "mb-4" : "";

    return `
      <li class="product-item splide__slide ${config.spacingMobile} ${
      config.spacingDesktop
    } ${marginClass}" 
          role="listitem"
          data-product-handle="${productHandle}"
          data-cnstrc-item-id="${product.data?.id || product.id}"
          data-cnstrc-item-name="${productTitle}"
          data-cnstrc-item-variation-id="${firstVariant.id || ""}"
          data-cnstrc-item-price="${finalPrice}">
        <div class="${config.cardStyle || ""}">
          <div class="img-wrapper position-relative mb-4${
            secondarySrc ? " has-hover-image" : ""
          }">
            <a href="${productUrl}">
              ${
                primaryBadgeResult.shouldShow
                  ? primaryBadgeResult.badgeHTML
                  : showSaleBadge && discountPercentage > 0
                  ? `<span class="img-badge-sale badge ${saleBadgeColor} text-white position-absolute">
                       ${config.productSave} ${discountPercentage}%
                     </span>`
                  : ""
              }
              ${
                primarySrc
                  ? `
                <img 
                  src="${primarySrc}"
                  srcset="${primarySrcset}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--primary img-fluid rounded ${
                    config.imgThumbnail
                  } png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="${loadingStrategy}"
                  fetchpriority="${fetchPriority}"
                  decoding="async"
                  style="aspect-ratio: ${aspectRatio};"
                />
                ${
                  secondarySrc
                    ? `
                <img 
                  src="${secondarySrc}"
                  srcset="${secondarySrcset}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--secondary img-fluid rounded ${config.imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="lazy"
                  fetchpriority="low"
                  decoding="async"
                  style="aspect-ratio: ${aspectRatio};"
                />`
                    : ""
                }
              `
                  : `
                <div class="placeholder-image bg-light d-flex align-items-center justify-content-center" 
                     style="aspect-ratio: ${aspectRatio}; min-height: 200px;">
                  <span class="text-muted">No Image</span>
                </div>
              `
              }
            </a>

            ${
              options.renderAddToCartDesktop
                ? options.renderAddToCartDesktop(product)
                : ""
            }
          </div>

          ${
            config.wishlistEnable && config.showWishlistBtn
              ? `
          <div class="wishlist-below-image mt-2 d-flex justify-content-end">
            <button class="btn-wishlist-add-remove btn" 
                    type="button"
                    data-product-handle="${productHandle}"
                    data-text-add="${config.wishlistAddText}"
                    data-text-remove="${config.wishlistRemoveText}"
                    aria-label="${config.wishlistAddText}"
                    onclick="addOrRemoveFromWishlist(this)"
                    data-cnstrc-btn="add_to_wishlist">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" 
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
          </div>
          `
              : ""
          }
          
          <div class="firstline-container">
            <div class="${config.cardStyle === "card" ? "card-body" : ""}">
              <h3 class="product-item-title mb-2 ${
                config.titleFontSizeMobile
              } ${config.titleFontSizeDesktop} ${config.titleTruncate}">
                <a href="${productUrl}" class="product-link position-relative d-block link-dark" tabindex="-1">
                  ${productTitle}
                </a>
              </h3>
              
              ${
                config.showRating
                  ? `<div class="product-rating-badge mb-2"></div>`
                  : ""
              }
              
              <p class="product-item-price product-item-price-carrossel mb-2" data-product-item-price>
              ${
                isOnSale
                  ? `
                    <span class="product-item-price-compare text-muted me-1">
                      <span class="visually-hidden">${config.productPriceRegular}&nbsp;</span>
                      <s>${formattedComparePrice}</s>
                    </span>
                    <span class="product-item-price-final">
                      <span class="visually-hidden">${config.productPriceSale}&nbsp;</span>
                      ${formattedPrice}
                    </span>
                  `
                  : `
                    <span class="product-item-price-final">
                      ${formattedPrice}
                    </span>
                  `
              }
            </p>

              ${
                fakeDiscountResult.shouldShow
                  ? fakeDiscountResult.messageHTML
                  : ""
              }

              ${swatchesResult.shouldShow ? swatchesResult.swatchesHTML : ""}
            </div>
          </div>
          
          ${
            options.renderAddToCartMobile
              ? options.renderAddToCartMobile(product)
              : ""
          }
        </div>
      </li>
    `;
  }

  /**
   * Get default configuration for product card rendering
   * @private
   * @returns {object} Default configuration
   */
  static _getDefaultConfig() {
    const imgOrientation =
      this.getThemeConfig("productCard.imgOrientation") || "square";
    const dimensions = this.getImageDimensions(imgOrientation);

    return {
      imgWidth: dimensions.width,
      imgHeight: dimensions.height,
      aspectRatio: `${dimensions.width}/${dimensions.height}`,
      cardStyle: this.getThemeConfig("productCard.style") || "",
      spacingMobile: this.getThemeConfig("productCard.spacingMobile") || "p-2",
      spacingDesktop:
        this.getThemeConfig("productCard.spacingDesktop") || "p-lg-3",
      imgThumbnail: this.getThemeConfig("productCard.imgThumbnail") || "",
      titleFontSizeMobile:
        this.getThemeConfig("productCard.titleFontSizeMobile") || "",
      titleFontSizeDesktop:
        this.getThemeConfig("productCard.titleFontSizeDesktop") || "",
      titleTruncate: this.getThemeConfig("productCard.titleTruncate")
        ? "text-truncate-2"
        : "",
      showWishlistBtn:
        this.getThemeConfig("productCard.showWishlistBtn") || false,
      showRating: this.getThemeConfig("productCard.showRating") || false,
      wishlistEnable: this.getThemeConfig("wishlist.enabled") || false,
      wishlistAddText:
        this.getThemeConfig("wishlist.translations.addToWishlist") ||
        "Add to wishlist",
      wishlistRemoveText:
        this.getThemeConfig("wishlist.translations.removeFromWishlist") ||
        "Remove from wishlist",
      showSaleBadge: this.getThemeConfig("productCard.showSaleBadge") || false,
      saleBadgeColor:
        this.getThemeConfig("productCard.saleBadgeColor") || "badge-danger",
      productSave:
        this.getThemeConfig("productCard.translations.save") || "Save",
      productPriceRegular:
        this.getThemeConfig("productCard.translations.regularPrice") ||
        "Regular price",
      productPriceSale:
        this.getThemeConfig("productCard.translations.salePrice") ||
        "Sale price",
    };
  }

  /**
   * Create a debounced function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  static debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Create a throttled function
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @returns {Function} Throttled function
   */
  static throttle(func, limit) {
    let inThrottle;
    return function executedFunction(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Check if element is in viewport
   * @param {Element} element - DOM element to check
   * @returns {boolean} True if element is in viewport
   */
  static isInViewport(element) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Safely parse JSON with fallback
   * @param {string} jsonString - JSON string to parse
   * @param {any} fallback - Fallback value if parsing fails
   * @returns {any} Parsed JSON or fallback
   */
  static safeJSONParse(jsonString, fallback = null) {
    try {
      return JSON.parse(jsonString);
    } catch {
      return fallback;
    }
  }

  /**
   * Generate unique ID
   * @param {string} prefix - Optional prefix for the ID
   * @returns {string} Unique ID
   */
  static generateId(prefix = "") {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return prefix
      ? `${prefix}-${timestamp}-${randomStr}`
      : `${timestamp}-${randomStr}`;
  }

  /**
   * Generate filters HTML from Constructor.io facets data
   * @param {Array} facets - Constructor.io facets array
   * @param {Object} currentFilters - Currently applied filters
   * @returns {string} Generated filters HTML
   */
  static generateFiltersFromFacets(facets, currentFilters = {}) {
    if (!facets || facets.length === 0) {
      return '<p class="text-muted">No filters available</p>';
    }

    const priceFilters = facets.filter(
      (f) => f.type === "range" && f.name === "price"
    );
    const otherFilters = facets.filter((f) => {
      const opts = f.options || f.values || [];
      return f.name !== "price" && opts.length > 0;
    });

    const allFilters = [...priceFilters, ...otherFilters];

    if (allFilters.length === 0) {
      return '<p class="text-muted">No filters available</p>';
    }

    return allFilters
      .map((facet, index) => {
        const isExpanded = index < 2;

        if (facet.type === "range" && facet.name === "price") {
          return this._generatePriceRangeFilter(
            facet,
            currentFilters,
            isExpanded
          );
        }

        return this._generateCheckboxFilter(facet, currentFilters, isExpanded);
      })
      .join("");
  }

  /**
   * Generate price range filter HTML
   * @private
   */
  static _generatePriceRangeFilter(facet, currentFilters, isExpanded) {
    const facetMin = parseFloat(facet.min.toFixed(2));
    const facetMax = parseFloat(facet.max.toFixed(2));

    let currentMin = currentFilters.price_min ?? facetMin;
    let currentMax = currentFilters.price_max ?? facetMax;

    if (
      currentFilters.price &&
      typeof currentFilters.price === "string" &&
      currentFilters.price.includes("-")
    ) {
      const parts = currentFilters.price.split("-");
      if (parts[0]) currentMin = parseFloat(parts[0]);
      if (parts[1]) currentMax = parseFloat(parts[1]);
    }

    const currencySymbol = window.Shopify?.currency?.symbol || "$";

    return `
      <details class="filter-group mb-4" ${isExpanded ? "open" : ""}>
        <summary class="filter-toggle py-3 cursor-pointer list-none">
          <span class="h6 mb-0">${this.sanitizeHTML(
            facet.display_name || facet.name
          )}</span>
        </summary>
        <div class="filter-options pt-3">
          <div class="price-range-inputs row mx-n2">
            <div class="col-6 px-2">
              <div class="input-group">
                <span class="input-group-text px-3">${currencySymbol}</span>
                <input
                  class="form-control constructor-price-input"
                  name="price_min"
                  value="${currentMin}"
                  type="number"
                  min="${facetMin}"
                  max="${facetMax}"
                  step="0.01"
                  placeholder="${facetMin}"
                  data-filter-name="price_min"
                  aria-label="Minimum price"
                >
              </div>
            </div>
            <div class="col-6 px-2">
              <div class="input-group">
                <span class="input-group-text px-3">${currencySymbol}</span>
                <input
                  class="form-control constructor-price-input"
                  name="price_max"
                  value="${currentMax}"
                  type="number"
                  min="${facetMin}"
                  max="${facetMax}"
                  step="0.01"
                  placeholder="${facetMax}"
                  data-filter-name="price_max"
                  aria-label="Maximum price"
                >
              </div>
            </div>
          </div>
          <div class="mt-3">
            <small class="text-muted">Range: ${currencySymbol}${facetMin} - ${currencySymbol}${facetMax}</small>
          </div>
        </div>
      </details>
    `;
  }

  /**
   * Generate checkbox filter HTML
   * @private
   */
  static _generateCheckboxFilter(facet, currentFilters, isExpanded) {
    const options = (facet.options || facet.values || [])
      .map((opt) => {
        const isChecked =
          currentFilters[facet.name]?.includes(opt.value) || opt.selected;
        return `
          <label class="d-flex align-items-center mb-2 cursor-pointer">
            <input type="checkbox" 
                   class="constructor-filter-checkbox me-2"
                   data-filter-name="${this.sanitizeHTML(facet.name)}"
                   data-filter-value="${this.sanitizeHTML(opt.value)}"
                   ${isChecked ? "checked" : ""}>
            <span class="small">${this.sanitizeHTML(
              opt.display_name || opt.value
            )} (${opt.count || 0})</span>
          </label>
        `;
      })
      .join("");

    return `
      <details class="filter-group mb-4" ${isExpanded ? "open" : ""}>
        <summary class="filter-toggle py-3 cursor-pointer list-none">
          <span class="h6 mb-0">${this.sanitizeHTML(
            facet.display_name || facet.name
          )}</span>
        </summary>
        <div class="filter-options pt-3">
          ${options}
        </div>
      </details>
    `;
  }

  /**
   * Generate sort options HTML from Constructor.io sort_options data
   * @param {Array} sortOptions - Constructor.io sort_options array
   * @returns {string} Generated sort dropdown HTML
   */
  static generateSortOptionsFromData(sortOptions) {
    if (!sortOptions || sortOptions.length === 0) {
      return "";
    }

    return sortOptions
      .map(
        (option) => `
        <div class="dropdown-item ${
          option.status === "selected" ? "selected" : ""
        }"
             data-value="${option.sort_by}"
             data-order="${option.sort_order}">
          ${option.display_name}
        </div>
      `
      )
      .join("");
  }

  /**
   * Sanitize HTML to prevent XSS
   * @param {string} str - String to sanitize
   * @returns {string} Sanitized string
   */
  static sanitizeHTML(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}

if (typeof window !== "undefined") {
  window.ConstructorUtils = ConstructorUtils;
}
